import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from 'react-router-dom';
import ModulesImage from "../../../asset/images/home/modules.png";
import { courseService } from "../../../services/courseService";
import { progressService } from "../../../services/progressService";
import { certificateService } from "../../../services/certificateService";
import api from "../../../services/api";

const DashboardModules = () => {
    const [searchParams] = useSearchParams();
    const courseId = searchParams.get('courseId');
    const [modules, setModules] = useState([]);
    const [lessons, setLessons] = useState({});
    const [moduleProgress, setModuleProgress] = useState({});
    const [activeModule, setActiveModule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [examStatus, setExamStatus] = useState(null);

    useEffect(() => {
        loadModules();
    }, [courseId]);

    useEffect(() => {
       
        if (activeModule && selectedCourseId) {
            const currentLessons = lessons[activeModule] || [];
            const needsFullDetails = currentLessons.length > 0 && !currentLessons[0].description;
            if (needsFullDetails || currentLessons.length === 0) {
                loadModuleLessons(activeModule);
            }
        }
    }, [activeModule, selectedCourseId, lessons]);

    useEffect(() => {
        if (selectedCourseId) {
            loadExamStatus();
        }
    }, [selectedCourseId]);

    const findActiveModule = (modulesData, lessonsData, progressMap) => {
       
        for (const module of modulesData) {
            const moduleLessons = lessonsData[module.id] || [];
            const progressInfo = progressMap[module.id];
            
           
            if (progressInfo) {
                const completed = progressInfo.completedLessons || 0;
                const total = progressInfo.totalLessons || 0;
                if (completed < total) {
                    return module.id;
                }
            }
            
            if (moduleLessons.length > 0) {
                const hasIncompleteLesson = moduleLessons.some(lesson => {
                    const progressStatus = lesson.progress?.status || lesson.progress;
                    return progressStatus !== 'COMPLETED' && lesson.isAccessible !== false;
                });
                if (hasIncompleteLesson) {
                    return module.id;
                }
            }
        }
        
        
        return modulesData.length > 0 ? modulesData[0].id : null;
    };

    const loadModules = async () => {
        setLoading(true);
        try {
           
            const coursesResult = await courseService.getCourses();
            if (coursesResult.success && coursesResult.data.courses.length > 0) {
                const targetCourseId = courseId || coursesResult.data.courses[0].id;
                setSelectedCourseId(targetCourseId);

              
                const modulesResult = await courseService.getCourseModules(targetCourseId);
                if (modulesResult.success) {
                    const modulesData = modulesResult.data.modules || [];
                    setModules(modulesData);

              
                    const progressResult = await progressService.getCourseProgress(targetCourseId);
                    const progressMap = {};
                    if (progressResult.success) {
                        progressResult.data.modules.forEach((module) => {
                            progressMap[module.moduleId] = module;
                        });
                        setModuleProgress(progressMap);
                    }
                    
                    
                    const lessonsSummaryResult = await courseService.getCourseLessonsSummary(targetCourseId);
                    const lessonsData = lessonsSummaryResult.success 
                        ? (lessonsSummaryResult.data.lessonsByModule || {})
                        : {};
                    
                    if (lessonsSummaryResult.success) {
                        setLessons(lessonsData);
                    }
                    
                    
                    if (modulesData.length > 0) {
                        const activeModuleId = findActiveModule(modulesData, lessonsData, progressMap);
                        setActiveModule(activeModuleId || modulesData[0].id);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load modules:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadModuleLessons = async (moduleId) => {
        if (!selectedCourseId) return;
        
        try {
            const lessonsResult = await courseService.getModuleLessons(selectedCourseId, moduleId);
            if (lessonsResult.success) {
                setLessons((prev) => ({
                    ...prev,
                    [moduleId]: lessonsResult.data.lessons || [],
                }));
            }
        } catch (error) {
            console.error('Failed to load lessons:', error);
        }
    };

    const loadExamStatus = async () => {
        if (!selectedCourseId) return;
        
        try {
            const response = await api.get(`/courses/${selectedCourseId}/exam/status`);
            if (response.data.success) {
                setExamStatus(response.data.data);
            }
        } catch (error) {
            setExamStatus(null);
        }
    };

    
    const formatTime = (minutes) => {
        if (!minutes) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        }
        return `${mins}m`;
    };

   
    const getGlobalLessonNumber = (moduleId, lessonOrder) => {
        if (!modules || !moduleId) return lessonOrder || 1;
        
        const currentModule = modules.find(m => m.id === moduleId);
        if (!currentModule) return lessonOrder || 1;
        
       
        let totalPreviousLessons = 0;
        modules.forEach(module => {
            if (module.order < currentModule.order) {
               
                const moduleLessons = lessons[module.id];
                if (moduleLessons && moduleLessons.length > 0) {
                    totalPreviousLessons += moduleLessons.length;
                } else {
                  
                    totalPreviousLessons += 2;
                }
            }
        });
        
       
        return totalPreviousLessons + (lessonOrder || 1);
    };

   
    const getModuleProgressInfo = (moduleId) => {
        const progress = moduleProgress[moduleId];
        if (!progress) {
            return { completed: 0, total: 0, percentage: 0 };
        }
        return {
            completed: progress.completedLessons || 0,
            total: progress.totalLessons || 0,
            percentage: progress.progressPercentage || 0,
        };
    };

    return (
        <>
            <style>
                {`
        .modules-container {
            height: calc(100vh - 200px);
            overflow-y: auto;
            margin-right: -10px; /* Negative margin to pull scrollbar to the edge */
            padding-right: 10px; /* Internal padding for content */
        }

        /* Webkit scrollbar styling */
        .modules-container::-webkit-scrollbar {
            width: 6px;
        }

        .modules-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }

        .modules-container::-webkit-scrollbar-thumb {
            background: #1A588B;
            border-radius: 10px;
        }

        .modules-container::-webkit-scrollbar-thumb:hover {
            background: #059669;
        }

        /* Firefox scrollbar styling */
        .modules-container {
            scrollbar-width: thin;
            scrollbar-color: #1A588B #f1f1f1;
        }

        /* Module details styling - matching screenshots exactly */
        .module-details {
            display: none;
        }

        .module-details.active {
            display: block;
        }

        .detail-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 14px;
            color: #6b7280;
        }

        .detail-title {
            font-size: 18px;
            color: #2d2f32;
        }

        .detail-date {
            font-size: 12px;
            color: #090a0b;
            background-color: #E2F2FF;
            padding: 10px;
            border-radius: 20px;
        }
        .lesson-style {
            font-size: 12px;
            color: #090a0b;
            background-color: #E2F2FF;
            padding: 10px 20px; /* top/bottom: 10px, left/right: 20px */
            border-radius: 20px;
            margin-left: 5px;
        }

        .lesson-section {
            margin-bottom: 24px;
        }

        .lesson-title {
            font-size: 1.5rem; /* Reduced from 45px to 24px */
            font-weight: 600;
            margin-bottom: 16px;
            line-height: 1.4;
        }

        .lesson-image {
            width: 100%;
            max-width: 600px;
            height: 280px;
            background-color: #e5e7eb;
            border-radius: 8px;
            margin: 20px auto;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .lesson-image img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        .lesson-info {
            display: flex;
            margin-bottom: 24px;
            font-size: 14px;
            color: #6b7280;
        }

        .start-lesson-btn {
            background: #059669;
            color: white;
            border: none;
            border-radius: 50px;
            padding: 16px 80px;
            font-size: 20px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            width: auto;
            display: inline-block;
            min-width: 320px;
        }

        .start-lesson-btn:hover {
            background: #047857;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
        }

        .review-lesson-btn {
            background: linear-gradient(135deg, #64748b 0%, #475569 100%);
            color: white;
            border: none;
            border-radius: 50px;
            padding: 16px 80px;
            font-size: 20px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 2px 4px rgba(71, 85, 105, 0.3);
            width: auto;
            display: inline-block;
            min-width: 320px;
        }

        .review-lesson-btn:hover {
            background: linear-gradient(135deg, #475569 0%, #334155 100%);
            box-shadow: 0 4px 6px rgba(71, 85, 105, 0.4);
            transform: translateY(-1px);
        }

        /* Settings button styling */
        .settings-button {
            width: 100%;
            border-radius: 12px;
            overflow: hidden;
            transition: all 0.3s ease;
            margin-bottom: 16px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            display: flex;
            justify-content: space-between;
            padding: 10px 20px;
        }

        .button-content {
            display: flex;
            flex-grow: 1;
        }

        .button-icon {
            width: 24px;
            height: 24px;
            margin-right: 16px;
        }

        .button-arrow {
            width: 20px;
            height: 20px;
        }

        /* Default state for buttons */
        .settings-button:not(.active) {
            background-color: white;
            border: 2px solid #1A588B;
        }

        .settings-button:not(.active) .button-icon {
            color: #1A588B;
        }

        .settings-button:not(.active) .button-content span,
        .settings-button:not(.active) .button-arrow {
            color: #1A588B;
        }

        /* Hover state for non-active buttons - looks like active button */
        .settings-button:not(.active):hover {
            background: linear-gradient(to right, #1A588B, #059669);
            color: white;
            border: none;
            border-radius: 16px;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .settings-button:not(.active):hover .button-icon,
        .settings-button:not(.active):hover .button-content span,
        .settings-button:not(.active):hover .button-arrow {
            color: white;
        }

        /* Active state for certificate button */
        .settings-button.active {
            background: linear-gradient(to right, #1A588B, #059669);
            color: white;
            border: none;
            border-radius: 16px;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* Hover state for active button - stays the same */
        .settings-button.active:hover {
            background: linear-gradient(to right, #1A588B, #059669);
            color: white;
            border: none;
            border-radius: 16px;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .settings-button.active .button-icon,
        .settings-button.active .button-content span,
        .settings-button.active .button-arrow {
            color: white;
        }

        .settings-button.active:hover .button-icon,
        .settings-button.active:hover .button-content span,
        .settings-button.active:hover .button-arrow {
            color: white;
        }

        /* Module progress info */
        .module-progress {
            font-size: 10px;
            margin-top: 4px;
        }

        /* Increase left sidebar width */
        @media (min-width: 1024px) {
            .grid-cols-1.lg\\\\:grid-cols-3 {
                grid-template-columns: 2fr 3fr;
            }
        }

        .modules-text{
            text-align: left !important;
        }
        .modules-text-style{
            font-size: 1rem; /* Reduced from 1.25rem */
            font-weight: 600;
        }
        .modules-bold{
            font-weight: bold;
        }
 `}
            </style>

        <div className="bg-[#E2F2FF] rounded-xl p-3 lg:p-3 shadow-md border border-blue-200">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Course Modules */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <h3 className="text-2xl font-bold mb-4 text-gray-600">
                                Course Modules
                            </h3>

                            {/* Modules Container with Scrollbar */}
                            <div className="modules-container">
                                {loading ? (
                                    <div className="text-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                                        <p className="mt-2 text-sm text-gray-600">Loading modules...</p>
                                    </div>
                                ) : modules.length > 0 ? (
                                    modules.map((module, index) => {
                                        const progressInfo = getModuleProgressInfo(module.id);
                                        const moduleLessons = lessons[module.id] || [];
                                        
                                        // Module icons (cycling through available icons)
                                        const iconPaths = [
                                            <path key="icon1" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />,
                                            <path key="icon2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
                                            <path key="icon3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />,
                                            <path key="icon4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />,
                                            <path key="icon5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />,
                                        ];
                                        
                                        return (
                                            <button
                                                key={module.id}
                                                className={`settings-button ${activeModule === module.id ? "active" : ""}`}
                                                onClick={() => setActiveModule(module.id)}
                                            >
                                                <div className="button-content">
                                                    <svg
                                                        className="button-icon"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        {iconPaths[index % iconPaths.length]}
                                                    </svg>
                                                    <div className="modules-text">
                                                        <span className="modules-text-style">{index + 1}. {module.title}</span>
                                                        <div className="module-progress">
                                                            {progressInfo.completed}/{progressInfo.total} Completed | Estimated Time: {formatTime(module.estimatedTime)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <svg
                                                    className="button-arrow"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M9 5l7 7-7 7"
                                                    />
                                                </svg>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No modules available</p>
                                    </div>
                                )}
                            </div>

                            {/* Summative Assessment Section */}
                            {examStatus && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-2xl font-bold mb-4 text-gray-600">
                                        Summative Assessment
                                    </h3>
                                    {examStatus.hasPassed ? (
                                        <div>
                                            <button
                                                className="settings-button"
                                                disabled
                                                style={{ opacity: 0.8, cursor: 'default' }}
                                            >
                                                <div className="button-content">
                                                    <svg
                                                        className="button-icon"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <div className="modules-text">
                                                        <span className="modules-text-style">Final Exam</span>
                                                        <div className="module-progress">
                                                            Passed ({examStatus.latestAttempt?.score || 0}%)
                                                        </div>
                                                    </div>
                                                </div>
                                                <svg
                                                    className="button-arrow"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                            </button>
                                            {examStatus.certificate && (
                                                <button 
                                                    className="settings-button mt-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                                                    onClick={async () => {
                                                        try {
                                                            const certResponse = await api.get(`/certificates/course/${selectedCourseId}`);
                                                            
                                                            if (certResponse.data.success && certResponse.data.data.certificate) {
                                                                const cert = certResponse.data.data.certificate;
                                                                
                                                                if (cert.id) {
                                                                    await certificateService.downloadCertificate(cert.id);
                                                                    return;
                                                                }
                                                            }
                                                            
                                                            window.location.href = `/dashboard/certificate?courseId=${selectedCourseId}`;
                                                        } catch (error) {
                                                            console.error('Error downloading certificate:', error);
                                                            window.location.href = `/dashboard/certificate?courseId=${selectedCourseId}`;
                                                        }
                                                    }}
                                                >
                                                    <div className="button-content">
                                                        <svg
                                                            className="button-icon"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <div className="modules-text">
                                                            <span className="modules-text-style">Download Certificate</span>
                                                            <div className="module-progress">
                                                                Certificate #{examStatus.certificate.certificateNumber}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <svg
                                                        className="button-arrow"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M9 5l7 7-7 7"
                                                        />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    ) : examStatus.canAccessExam ? (
                                        <Link to={`/dashboard/exam?courseId=${selectedCourseId}`}>
                                            <button className="settings-button">
                                                <div className="button-content">
                                                    <svg
                                                        className="button-icon"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <div className="modules-text">
                                                        <span className="modules-text-style">Final Exam</span>
                                                        <div className="module-progress">
                                                            {examStatus.latestAttempt
                                                                ? `Last Score: ${examStatus.latestAttempt.score}%`
                                                                : 'Available'
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                                <svg
                                                    className="button-arrow"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M9 5l7 7-7 7"
                                                    />
                                                </svg>
                                            </button>
                                        </Link>
                                    ) : (
                                        <button
                                            className="settings-button"
                                            disabled
                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                        >
                                            <div className="button-content">
                                                <svg
                                                    className="button-icon"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <div className="modules-text">
                                                    <span className="modules-text-style">Final Exam</span>
                                                    <div className="module-progress">
                                                        Locked - Complete all lessons
                                                    </div>
                                                </div>
                                            </div>
                                            <svg
                                                className="button-arrow"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Module Details */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-md p-6">
                            {loading ? (
                                <div className="text-center py-16">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Loading module details...</p>
                                </div>
                            ) : activeModule && modules.find((m) => m.id === activeModule) ? (
                                (() => {
                                    const currentModule = modules.find((m) => m.id === activeModule);
                                    const moduleLessons = lessons[activeModule] || [];
                                    const progressInfo = getModuleProgressInfo(activeModule);
                                    
                                    return (
                                        <div className="module-details active">
                                            <div className="detail-header">
                                                <div className="detail-title">Module Details</div>
                                            </div>

                                            {moduleLessons.length > 0 ? (
                                                moduleLessons.map((lesson, index) => {
                                                    const progressStatus = lesson.progress?.status || (lesson.progress === 'COMPLETED' ? 'COMPLETED' : null);
                                                    const isCompleted = progressStatus === 'COMPLETED';
                                                    const isAccessible = lesson.isAccessible !== false && !isCompleted;
                                                    const globalLessonNumber = getGlobalLessonNumber(activeModule, lesson.order || index + 1);
                                                    
                                                    return (
                                                        <div key={lesson.id} className="lesson-section">
                                                            <div className="lesson-title">Lesson {globalLessonNumber}: {lesson.title}</div>
                                                            <div className="lesson-image">
                                                                <img
                                                                    src={lesson.profileImage || ModulesImage}
                                                                    alt={lesson.title}
                                                                    className="w-full h-full object-contain"
                                                                />
                                                            </div>

                                                            <div className="lesson-info">
                                                                <div className="lesson-style">
                                                                    {isCompleted ? (
                                                                        <span className="flex items-center gap-2">
                                                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                            Completed
                                                                        </span>
                                                                    ) : isAccessible ? (
                                                                        'Available'
                                                                    ) : (
                                                                        <span className="flex items-center gap-2">
                                                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                            </svg>
                                                                            Locked
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="lesson-style">
                                                                    Duration: {formatTime(lesson.estimatedTime)}
                                                                </div>
                                                            </div>

                                                            {!isAccessible && !isCompleted && (
                                                                <div className="mb-4 p-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                                                                    <p className="text-sm text-yellow-700">
                                                                        <strong>Locked:</strong> Complete all previous lessons to unlock this lesson.
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {isAccessible ? (
                                                                <div className="flex justify-center">
                                                                    <Link 
                                                                        to={`/dashboard/modules/lesson-read?courseId=${selectedCourseId}&moduleId=${activeModule}&lessonId=${lesson.id}`}
                                                                    >
                                                                        <button className="start-lesson-btn">
                                                                            {index === 0 ? 'Start Lesson' : 'Continue Lesson'}
                                                                        </button>
                                                                    </Link>
                                                                </div>
                                                            ) : (
                                                                <div className="flex justify-center">
                                                                    <button 
                                                                        className="start-lesson-btn" 
                                                                        disabled
                                                                        style={{ 
                                                                            opacity: 0.5, 
                                                                            cursor: 'not-allowed',
                                                                            background: isCompleted
                                                                                ? 'linear-gradient(to right, #16a34a, #15803d)'
                                                                                : 'linear-gradient(to right, #9ca3af, #6b7280)'
                                                                        }}
                                                                    >
                                                                    <span className="flex items-center justify-center gap-2">
                                                                        {isCompleted ? (
                                                                            <>
                                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                                </svg>
                                                                                Lesson Completed
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                                </svg>
                                                                                Lesson Locked
                                                                            </>
                                                                        )}
                                                                    </span>
                                                                </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : null}

                                            {moduleLessons.length === 0 && (
                                                <div className="lesson-section">
                                                    <div className="lesson-title">{currentModule.title}</div>
                                                    <div className="lesson-image">
                                                        <img
                                                            src={ModulesImage}
                                                            alt={currentModule.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>

                                                    <div className="lesson-info">
                                                        <div className="lesson-style">
                                                            Progress: {progressInfo.completed}/{progressInfo.total} lessons
                                                        </div>
                                                        <div className="lesson-style">
                                                            Estimated Time: {formatTime(currentModule.estimatedTime)}
                                                        </div>
                                                    </div>

                                                    {currentModule.description && (
                                                        <p className="text-gray-600 mt-4">{currentModule.description}</p>
                                                    )}

                                                    <div className="text-center py-4 text-gray-500">
                                                        <p>Lessons are being loaded...</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()
                            ) : (
                                <div className="text-center py-16 text-gray-500">
                                    <p>Select a module to view details</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default DashboardModules;
